import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import Iconify from "../../../components/iconify";
import { LineItemsDetailTable } from "./ShopOrderComponents";
import {
  ORDER_STATUS_OPTIONS,
  ORDER_STATUSES_EDITABLE,
  FULFILLMENT_TYPE_META,
  getStatusMeta,
  getFulfillmentStatusMeta,
  getContractName,
  getContractEmail,
  getContractAddress,
  buildCompactSummary,
  toCurrency,
} from "./shopUtils";

export default function ShopOrdersDesktopTable({
  visibleOrders,
  selectedOrderIds,
  expandedOrderId,
  onToggleOrderSelection,
  onToggleExpand,
  onOrderHeaderSort,
  getOrderSortMeta,
  onOrderStatusChange,
  onCancelSingleOrder,
  onDeleteSingleOrder,
  onEditOrder,
  onUpdateFulfillment,
  allOrdersSelected,
  partiallySelected,
  onToggleSelectAll,
  runningAdminAction,
}) {
  const sortCell = (field, label, align) => {
    const meta = getOrderSortMeta(field);
    return (
      <TableCell
        align={align}
        sortDirection={meta.active ? meta.direction : false}
      >
        <TableSortLabel
          active={meta.active}
          direction={meta.direction}
          onClick={() => onOrderHeaderSort(field)}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allOrdersSelected}
                  indeterminate={partiallySelected}
                  onChange={(e) => onToggleSelectAll(e.target.checked)}
                />
              </TableCell>
              {sortCell("date", "Datum")}
              {sortCell("member", "Mitglied / Vertrag")}
              {sortCell("items", "Positionen")}
              <TableCell align="right">Menge</TableCell>
              {sortCell("total", "Gesamt", "right")}
              <TableCell>Status</TableCell>
              <TableCell>Versand</TableCell>
              <TableCell align="center" sx={{ width: 120 }}>
                Aktionen
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleOrders.map((order) => {
              const isExpanded = expandedOrderId === order._id;
              const isSelected = selectedOrderIds.includes(order._id);
              const statusMeta = getStatusMeta(order.status);
              const fMeta = getFulfillmentStatusMeta(order.fulfillment?.status);
              const fType = order.fulfillment?.type || "shipping";
              const canEdit = ORDER_STATUSES_EDITABLE.has(order.status);
              const totalQty = (order.lineItems || []).reduce(
                (s, li) => s + Number(li.quantity || 0),
                0,
              );

              return [
                <TableRow
                  key={order._id}
                  hover
                  selected={isSelected}
                  sx={{
                    "& > *": {
                      borderBottom: isExpanded ? "unset" : undefined,
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) =>
                        onToggleOrderSelection(order._id, e.target.checked)
                      }
                    />
                  </TableCell>

                  {/* Datum */}
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2">
                      {dayjs(order.createdAt).format("DD.MM.YYYY HH:mm")}
                    </Typography>
                  </TableCell>

                  {/* Mitglied / Vertrag */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {getContractName(order)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {getContractEmail(order)}
                    </Typography>
                  </TableCell>

                  {/* Positionen */}
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 280,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {buildCompactSummary(order.lineItems)}
                    </Typography>
                  </TableCell>

                  {/* Menge */}
                  <TableCell align="right">
                    <Typography variant="body2">{totalQty}</Typography>
                  </TableCell>

                  {/* Gesamt */}
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {toCurrency(order?.totals?.grandTotal)}
                    </Typography>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Tooltip title={statusMeta.tooltip || ""}>
                      <Chip
                        size="small"
                        color={statusMeta.color}
                        label={statusMeta.label}
                      />
                    </Tooltip>
                  </TableCell>

                  {/* Versand */}
                  <TableCell>
                    {fType !== "none" && (
                      <Tooltip
                        title={FULFILLMENT_TYPE_META[fType]?.label || ""}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          color={fMeta.color}
                          label={fMeta.label}
                          icon={
                            <Iconify
                              icon={
                                FULFILLMENT_TYPE_META[fType]?.icon ||
                                "mdi:truck-delivery-outline"
                              }
                              width={14}
                            />
                          }
                        />
                      </Tooltip>
                    )}
                  </TableCell>

                  {/* Aktionen */}
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      spacing={0.25}
                      justifyContent="center"
                    >
                          <Tooltip title="Status ändern">
                        <FormControl size="small">
                          <Select
                            value={order.status}
                            onChange={(e) =>
                              onOrderStatusChange(order._id, e.target.value)
                            }
                            variant="outlined"
                            sx={{
                              fontSize: "0.7rem",
                              "& .MuiSelect-select": {
                                py: 0.25,
                                pr: "24px !important",
                                pl: 0.75,
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${statusMeta.color}.main`,
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${statusMeta.color}.dark`,
                              },
                            }}
                            renderValue={() => (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  color: `${statusMeta.color}.main`,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {statusMeta.label}
                              </Typography>
                            )}
                          >
                            {ORDER_STATUS_OPTIONS.map((status) => {
                              const meta = getStatusMeta(status);
                              return (
                                <MenuItem key={status} value={status}>
                                  <Chip size="small" color={meta.color} label={meta.label} />
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Tooltip>
                      {canEdit && (
                        <Tooltip title="Bearbeiten">
                          <IconButton
                            size="small"
                            onClick={() => onEditOrder(order)}
                            disabled={runningAdminAction}
                          >
                            <Iconify icon="mdi:pencil-outline" width={16} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => onToggleExpand(order._id)}
                      >
                        <Iconify
                          icon={
                            isExpanded
                              ? "eva:chevron-up-fill"
                              : "eva:chevron-down-fill"
                          }
                          width={18}
                        />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>,

                /* ── Detail row ── */
                <TableRow key={`${order._id}-detail`}>
                  <TableCell
                    colSpan={9}
                    sx={{
                      py: 0,
                      px: 0,
                      borderBottom: isExpanded ? undefined : "unset",
                    }}
                  >
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: "action.hover" }}>
                        <Stack spacing={2}>
                          <LineItemsDetailTable lineItems={order.lineItems} />
                          <Divider />
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={2}
                            alignItems={{ md: "center" }}
                            justifyContent="space-between"
                          >
                            <Stack spacing={0.25}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                ID: {order._id}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                E-Mail: {getContractEmail(order)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Adresse: {getContractAddress(order)}
                              </Typography>
                              {order.fulfillment?.trackingNumber && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Sendungsnr.:{" "}
                                  {order.fulfillment.trackingNumber}
                                </Typography>
                              )}
                            </Stack>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                            >
                              {order.fulfillment?.type === "local" &&
                                order.fulfillment?.status !== "handed_out" &&
                                order.status !== "canceled" && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    disabled={runningAdminAction}
                                    onClick={() =>
                                      onUpdateFulfillment({
                                        id: order._id,
                                        fulfillmentStatus: "handed_out",
                                      })
                                    }
                                    startIcon={
                                      <Iconify
                                        icon="mdi:hand-extended-outline"
                                        width={16}
                                      />
                                    }
                                  >
                                    Als ausgegeben markieren
                                  </Button>
                                )}
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                disabled={
                                  order.status === "canceled" ||
                                  runningAdminAction
                                }
                                onClick={() => onCancelSingleOrder(order)}
                                startIcon={
                                  <Iconify icon="mdi:cancel" width={16} />
                                }
                              >
                                Stornieren
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="error"
                                disabled={runningAdminAction}
                                onClick={() => onDeleteSingleOrder(order)}
                                startIcon={
                                  <Iconify
                                    icon="eva:trash-2-outline"
                                    width={16}
                                  />
                                }
                              >
                                Löschen
                              </Button>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>,
              ];
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
